import { useCallback, useMemo, useState, type ChangeEvent } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import { alpha, useTheme } from "@mui/material/styles";
import FormField from "layouts/pages/account/components/FormField";
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RemoveShoppingCartOutlinedIcon from "@mui/icons-material/RemoveShoppingCartOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { setMessage, useMaterialUIController } from "context";
import { LojaDataGrid, LojaPageContainer } from "../common";
import { useEstoqueLoja } from "hooks/useEstoqueLoja";
import type {
  IEstoqueItemRow,
  TipoMovimentacaoEstoque,
} from "interfaces/loja/estoque";
import AjusteEstoqueDialog from "./components/AjusteEstoqueDialog";
import MDButton from "components/MDButton";
import { formatCurrency } from "helpers/utils";
import { exportEstoqueBaixoPdf } from "./helpers/export-estoque-baixo-pdf";

const ESTOQUE_ACCENT = "#F96030";

const TIPO_MOV_OPTIONS: {
  value: TipoMovimentacaoEstoque | "";
  label: string;
}[] = [
  { value: "", label: "Todos os tipos" },
  { value: "ENTRADA", label: "Entrada" },
  { value: "SAIDA", label: "Saída" },
  { value: "RESERVA", label: "Reserva" },
  { value: "LIBERACAO", label: "Liberação" },
  { value: "AJUSTE", label: "Ajuste" },
  { value: "CANCELAMENTO", label: "Cancelamento" },
];

const TIPO_MOV_SHORT: Partial<Record<TipoMovimentacaoEstoque, string>> = {
  ENTRADA: "Entrada",
  SAIDA: "Saída",
  RESERVA: "Reserva",
  LIBERACAO: "Liberação",
  AJUSTE: "Ajuste",
  CANCELAMENTO: "Cancelamento",
};

function KpiTile({
  title,
  value,
  subtitle,
  icon,
  accent,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  accent: string;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(accent, isDark ? 0.35 : 0.25),
        background: isDark
          ? alpha(accent, 0.08)
          : `linear-gradient(135deg, ${alpha(accent, 0.07)} 0%, ${alpha(
              accent,
              0.02
            )} 100%)`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 12px 24px ${alpha(accent, 0.12)}`,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(accent, 0.18),
            color: accent,
          }}
        >
          {icon}
        </Box>
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ mt: 0.5, lineHeight: 1.1 }}
          >
            {value}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function LojaEstoquePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [, dispatchCtx] = useMaterialUIController();
  const [exportingPdf, setExportingPdf] = useState(false);
  const mode = theme.palette.mode;
  const accentPrimary = theme.palette.primary.main;
  const accentInfo = theme.palette.info.main;
  const accentWarning = theme.palette.warning.main;
  const accentError = theme.palette.error.main;

  const [tab, setTab] = useState(0);
  const [ajusteRow, setAjusteRow] = useState<IEstoqueItemRow | null>(null);

  const {
    resumo,
    resumoLoading,
    itensRows,
    itensTotal,
    itensPage,
    itensLoading,
    itensPageSize,
    buscaItensDraft,
    setBuscaItensDraft,
    somenteBaixo,
    setSomenteBaixo,
    limiteBaixo,
    setLimiteBaixo,
    onItensPagination,
    movRows,
    movTotal,
    movPage,
    movLoading,
    movPageSize,
    movTipo,
    setMovTipo,
    movSku,
    setMovSku,
    movDataInicio,
    setMovDataInicio,
    movDataFim,
    setMovDataFim,
    onMovPagination,
    aplicarAjuste,
  } = useEstoqueLoja();

  const borderSoft = useMemo(
    () =>
      mode === "dark" ? alpha(accentPrimary, 0.35) : alpha(accentPrimary, 0.18),
    [mode, accentPrimary]
  );

  const tipoMovOption =
    TIPO_MOV_OPTIONS.find((o) => o.value === movTipo) ?? TIPO_MOV_OPTIONS[0];

  const fieldBgSx =
    mode === "dark"
      ? { background: theme.palette.background.paper }
      : { background: "white" };

  const itensColumns: GridColDef<IEstoqueItemRow>[] = useMemo(
    () => [
      {
        field: "sku",
        headerName: "SKU",
        width: 200,
        renderCell: (p: GridRenderCellParams<IEstoqueItemRow>) => (
          <Link
            component={RouterLink}
            to={`/loja/produtos/editar/${p.row.produtoId}`}
            underline="hover"
            fontWeight={600}
            color="primary"
            onClick={(e) => e.stopPropagation()}
          >
            {p.row.sku}
          </Link>
        ),
      },
      {
        field: "produtoNome",
        headerName: "Produto",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "precoCusto",
        headerName: "Preço de custo",
        width: 120,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: (p: GridRenderCellParams<IEstoqueItemRow>) =>
          p.row.precoCusto != null && Number.isFinite(Number(p.row.precoCusto))
            ? formatCurrency(p.row.precoCusto)
            : "—",
      },
      {
        field: "precoVenda",
        headerName: "Preço de venda",
        width: 120,
        align: "right",
        headerAlign: "right",
        sortable: false,
        renderCell: (p: GridRenderCellParams<IEstoqueItemRow>) =>
          p.row.precoVenda != null && Number.isFinite(Number(p.row.precoVenda))
            ? formatCurrency(p.row.precoVenda)
            : "—",
      },
      {
        field: "estoque",
        headerName: "Qtd",
        width: 100,
        type: "number",
        align: "right",
        headerAlign: "right",
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        sortable: false,
        renderCell: (p: GridRenderCellParams<IEstoqueItemRow>) => {
          const low = p.row.estoque > 0 && p.row.estoque <= limiteBaixo;
          const zero = p.row.estoque <= 0;
          return (
            <Chip
              size="small"
              label={zero ? "Zerado" : low ? "Baixo" : "OK"}
              color={zero ? "error" : low ? "warning" : "success"}
              variant={zero || low ? "filled" : "outlined"}
              sx={{ fontWeight: 600 }}
            />
          );
        },
      },
      {
        field: "acoes",
        headerName: "",
        width: 130,
        sortable: false,
        renderCell: (p: GridRenderCellParams<IEstoqueItemRow>) => (
          <MDButton
            variant="gradient"
            color="light"
            size="small"
            onClick={() => setAjusteRow(p.row)}
          >
            Ajustar
          </MDButton>
        ),
      },
    ],
    [limiteBaixo]
  );

  const movColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: "createAt",
        headerName: "Data",
        width: 160,
        renderCell: (p) =>
          p.row.createAt
            ? format(parseISO(p.row.createAt), "dd/MM/yyyy HH:mm", {
                locale: ptBR,
              })
            : "—",
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 130,
        renderCell: (p) => (
          <Chip
            label={
              TIPO_MOV_SHORT[p.row.tipo as TipoMovimentacaoEstoque] ??
              p.row.tipo
            }
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "quantidade",
        headerName: "Qtd",
        width: 90,
        type: "number",
        align: "right",
      },
      { field: "sku", headerName: "SKU", width: 130 },
      {
        field: "produtoNome",
        headerName: "Produto",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "variacaoRotulo",
        headerName: "Variação",
        width: 140,
        renderCell: (p) => p.row.variacaoRotulo ?? "—",
      },
      {
        field: "pedidoId",
        headerName: "Pedido",
        width: 120,
        renderCell: (p) =>
          p.row.pedidoId ? (
            <Button
              size="small"
              onClick={() => navigate(`/loja/pedidos/${p.row.pedidoId}`)}
            >
              Ver
            </Button>
          ) : (
            "—"
          ),
      },
      {
        field: "observacao",
        headerName: "Observação",
        flex: 1,
        minWidth: 200,
      },
    ],
    [navigate]
  );

  const handleItensPage = (m: GridPaginationModel) => {
    onItensPagination(m);
  };

  const handleMovPage = (m: GridPaginationModel) => {
    onMovPagination(m);
  };

  const handleExportEstoqueBaixoPdf = useCallback(async () => {
    setExportingPdf(true);
    try {
      const total = await exportEstoqueBaixoPdf(limiteBaixo);
      setMessage(dispatchCtx, {
        open: true,
        severity: "success",
        text: `PDF gerado com ${total} item(ns) de estoque baixo ou zerado.`,
      });
    } catch (err) {
      const text =
        err instanceof Error ? err.message : "Não foi possível gerar o PDF.";
      setMessage(dispatchCtx, {
        open: true,
        severity: err instanceof Error && err.message.includes("Nenhum") ? "info" : "error",
        text,
      });
    } finally {
      setExportingPdf(false);
    }
  }, [limiteBaixo, dispatchCtx]);

  return (
    <LojaPageContainer
      title="Controle de estoque"
      subtitle="Visão consolidada de SKUs, alertas e histórico de movimentações"
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<PictureAsPdfIcon />}
          disabled={exportingPdf || resumoLoading}
          onClick={() => void handleExportEstoqueBaixoPdf()}
          sx={{
            background: `linear-gradient(195deg, ${ESTOQUE_ACCENT}, #e04a1a)`,
          }}
        >
          {exportingPdf ? "Gerando…" : "Exportar estoque baixo (PDF)"}
        </MDButton>
      }
    >
      <Stack spacing={3}>
        <Box
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            background:
              mode === "dark"
                ? `linear-gradient(125deg, ${alpha(accentPrimary, 0.2)} 0%, ${alpha(
                    theme.palette.background.paper,
                    0.9
                  )} 55%)`
                : `linear-gradient(125deg, ${alpha(accentPrimary, 0.12)} 0%, ${alpha(
                    accentInfo,
                    0.06
                  )} 100%)`,
            border: `1px solid ${alpha(accentPrimary, 0.15)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="overline" color="primary" fontWeight={700}>
                Operações &amp; inventário
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                Painel de estoque
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 560 }}
              >
                Monitore unidades disponíveis, identifique rupturas e acompanhe
                entradas, saídas e ajustes registrados no sistema.
              </Typography>
            </Box>
            <Chip
              icon={<TuneOutlinedIcon />}
              label={`Alerta ≤ ${limiteBaixo} un.`}
              color="default"
              variant="outlined"
            />
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiTile
                title="SKUs cadastrados"
                value={resumoLoading ? "…" : (resumo?.totalSkus ?? "—")}
                subtitle="Produtos e variações"
                accent={accentPrimary}
                icon={<SummarizeOutlinedIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiTile
                title="Unidades em estoque"
                value={resumoLoading ? "…" : (resumo?.totalUnidades ?? "—")}
                subtitle="Soma de todas as posições"
                accent={accentInfo}
                icon={<Inventory2OutlinedIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiTile
                title="Itens em baixa"
                value={resumoLoading ? "…" : (resumo?.itensBaixoEstoque ?? "—")}
                subtitle={`Entre 1 e ${limiteBaixo} un.`}
                accent={accentWarning}
                icon={<WarningAmberOutlinedIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiTile
                title="Ruptura (zero)"
                value={resumoLoading ? "…" : (resumo?.itensZerados ?? "—")}
                subtitle="Requer reposição"
                accent={accentError}
                icon={<RemoveShoppingCartOutlinedIcon />}
              />
            </Grid>
          </Grid>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ mt: 4 }}
          >
            <TextField
              type="number"
              size="small"
              variant="outlined"
              label='Limite "baixo estoque" (KPI e alertas)'
              value={limiteBaixo}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLimiteBaixo(Number(e.target.value) || 5)
              }
              inputProps={{ min: 1, max: 9999 }}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 200,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: alpha(
                    theme.palette.background.paper,
                    mode === "dark" ? 0.18 : 0.5
                  ),
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(
                    accentPrimary,
                    mode === "dark" ? 0.35 : 0.28
                  ),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(accentPrimary, 0.5),
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: accentPrimary,
                },
              }}
            />
          </Stack>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${borderSoft}`,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              px: 1,
              "& .MuiTab-root": { fontWeight: 700, py: 2 },
            }}
          >
            <Tab
              icon={<Inventory2OutlinedIcon />}
              iconPosition="start"
              label="SKUs & saldos"
            />
            <Tab
              icon={<SwapHorizIcon />}
              iconPosition="start"
              label="Movimentações"
            />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {tab === 0 && (
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <FormField
                    variant="outlined"
                    label="Buscar"
                    placeholder="Nome do produto ou SKU…"
                    fullWidth
                    value={buscaItensDraft}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setBuscaItensDraft(e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldBgSx}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={somenteBaixo}
                        onChange={(_, c) => setSomenteBaixo(c)}
                        color="warning"
                      />
                    }
                    label="Só baixo estoque"
                  />
                </Stack>
                <LojaDataGrid
                  rows={itensRows}
                  columns={itensColumns}
                  loading={itensLoading}
                  getRowId={(r) => r.id}
                  paginationMode="server"
                  rowCount={itensTotal}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{
                    page: itensPage - 1,
                    pageSize: itensPageSize,
                  }}
                  onPaginationModelChange={handleItensPage}
                  disableRowSelectionOnClick
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: alpha(theme.palette.grey[500], 0.08),
                      fontWeight: 700,
                    },
                  }}
                />
              </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={2}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      size="small"
                      options={TIPO_MOV_OPTIONS}
                      value={tipoMovOption}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      onChange={(_, opt) => setMovTipo(opt?.value ?? "")}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          variant="outlined"
                          label="Tipo"
                          InputLabelProps={{ shrink: true }}
                          sx={fieldBgSx}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormField
                      variant="outlined"
                      label="SKU"
                      placeholder="Filtrar por SKU"
                      fullWidth
                      value={movSku}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setMovSku(e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={fieldBgSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormField
                      variant="outlined"
                      type="date"
                      label="Data (início)"
                      fullWidth
                      value={movDataInicio}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setMovDataInicio(e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={fieldBgSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormField
                      variant="outlined"
                      type="date"
                      label="Data (fim)"
                      fullWidth
                      value={movDataFim}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setMovDataFim(e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={fieldBgSx}
                    />
                  </Grid>
                </Grid>
                <LojaDataGrid
                  rows={movRows}
                  columns={movColumns}
                  loading={movLoading}
                  getRowId={(r) => r.id}
                  paginationMode="server"
                  rowCount={movTotal}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{
                    page: movPage - 1,
                    pageSize: movPageSize,
                  }}
                  onPaginationModelChange={handleMovPage}
                  disableRowSelectionOnClick
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: alpha(theme.palette.grey[500], 0.08),
                      fontWeight: 700,
                    },
                  }}
                />
              </Stack>
            )}
          </Box>
        </Paper>
      </Stack>

      <AjusteEstoqueDialog
        open={Boolean(ajusteRow)}
        row={ajusteRow}
        onClose={() => setAjusteRow(null)}
        onSubmit={aplicarAjuste}
      />
    </LojaPageContainer>
  );
}
